import { localTime, parseRouteParamAsNumber } from '@helpers';
import type {
  MachineStatisticsOeeTeepRequestDto,
  MachineStatisticsRequestDto,
  PaginationDto,
  StatisticsPeriodsDto,
} from '@api/domain/types';
import { STATISTICS_PERIOD as PERIOD, STATISTICS_TYPE as STATS_TYPE } from '@api/modules/statistics/types';
import { DATE_FORMAT } from '@v2/pages/common/constants/base.constants';
import { TimePeriod } from '@v2/pages/machines/common/enums';
import { DEFAULT_LIST_PAGINATION } from '@reducers/statistics/constants';
import type { MachineStatisticsInput, OeeTeepStatisticsInput, StatisticsPeriodInput } from '@reducers/statistics/types';

const toPositiveNumber = (value: number | string | null | undefined): number | undefined => {
  if (value == null || value === '') return undefined;

  const numberValue = parseRouteParamAsNumber(value);
  if (typeof numberValue !== 'number') return undefined;

  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : undefined;
};

const normalizePagination = (pagination?: PaginationDto | null): PaginationDto => ({
  pageNumber: toPositiveNumber(pagination?.pageNumber) ?? DEFAULT_LIST_PAGINATION.pageNumber,
  pageSize: toPositiveNumber(pagination?.pageSize) ?? DEFAULT_LIST_PAGINATION.pageSize,
  includeCount: pagination?.includeCount ?? DEFAULT_LIST_PAGINATION.includeCount,
});

const normalizePeriod = (period: StatisticsPeriodInput): StatisticsPeriodsDto => {
  switch (period) {
    case TimePeriod.DAY:
    case PERIOD.Day:
      return PERIOD.Day;
    case TimePeriod.WEEK:
    case PERIOD.Week:
      return PERIOD.Week;
    case TimePeriod.MONTH:
    case PERIOD.Month:
      return PERIOD.Month;
    case TimePeriod.YEAR:
    case PERIOD.Year:
      return PERIOD.Year;
    case TimePeriod.EIGHT_WEEKS:
    case PERIOD.MultipleWeeks:
      return PERIOD.MultipleWeeks;
    default:
      throw new Error(`Unsupported statistics period: ${period}`);
  }
};

const normalizeDate = (date: string): string => localTime(date).format(DATE_FORMAT);

const buildScopeParams = ({ allSelected, forAllMachines, machineGroupId, machineId }: OeeTeepStatisticsInput) => {
  const shouldAggregate = allSelected ?? forAllMachines ?? false;
  const normalizedMachineGroupId = toPositiveNumber(machineGroupId);

  if (shouldAggregate) {
    return {
      forAllMachines: true,
      ...(normalizedMachineGroupId ? { machineGroupId: normalizedMachineGroupId } : {}),
    };
  }

  const normalizedMachineId = toPositiveNumber(machineId);
  if (normalizedMachineId) {
    return { machineId: normalizedMachineId };
  }

  return normalizedMachineGroupId ? { machineGroupId: normalizedMachineGroupId } : {};
};

const buildCommonParams = (input: OeeTeepStatisticsInput) => {
  const period = normalizePeriod(input.period);
  const requestedWeeks = toPositiveNumber(input.numberOfWeeks);

  return {
    date: normalizeDate(input.date),
    period,
    ...(period === PERIOD.MultipleWeeks ? { numberOfWeeks: requestedWeeks ?? 8 } : {}),
    ...buildScopeParams(input),
  };
};

const buildNcFileParams = ({ ncFileId }: MachineStatisticsInput) => {
  const normalizedNcFileId = toPositiveNumber(ncFileId);
  return normalizedNcFileId ? { ncFileId: normalizedNcFileId } : {};
};

export const buildStatsListParams = (input: MachineStatisticsInput): MachineStatisticsRequestDto => ({
  $type: STATS_TYPE.wholePeriod,
  ...buildCommonParams(input),
  ...buildNcFileParams(input),
  pagination: normalizePagination(input.pagination),
});

export const buildStatsGraphParams = (input: MachineStatisticsInput): MachineStatisticsRequestDto => ({
  $type: STATS_TYPE.perPeriod,
  ...buildCommonParams(input),
  ...buildNcFileParams(input),
});

// Shared input builder for chart hooks that call the per-period machine stats thunk.
// Treats missing/empty id OR explicit allSelected=true as the aggregate ("all machines") signal.
export const buildMachineStatsGraphParams = (
  period: StatisticsPeriodInput,
  date: string,
  id: string | number | null | undefined,
  allSelected: boolean
): MachineStatisticsInput => ({
  period,
  date,
  ...(allSelected || id == null || id === '' ? { allSelected: true } : { machineId: id }),
});

export const buildOeeTeepListParams = (input: OeeTeepStatisticsInput): MachineStatisticsOeeTeepRequestDto => ({
  $type: STATS_TYPE.wholePeriod,
  ...buildCommonParams(input),
  pagination: normalizePagination(input.pagination),
});

export const buildOeeTeepGraphParams = (input: OeeTeepStatisticsInput): MachineStatisticsOeeTeepRequestDto => ({
  $type: STATS_TYPE.perPeriod,
  ...buildCommonParams(input),
});
